// "Tranzaksiyalar" — `GET /api/ledger/entries`, BUTUN jurnal bo'ylab.
//
// ⚠️ `accounts/{id}/entries` (bitta hisob ko'chirmasi, `ledgerApi.ts`) dan
// FARQI: bu yerda qator BARCHA hisoblardan keladi va har qator qarshi
// tomonini ham olib yuradi. Shakl `ledger.journal.dto.JournalRow` dan
// o'lchandi, taxmin emas.
//
// ⚠️ `total` YO'Q — faqat `hasNext` (`LedgerPage` rekordi: `COUNT(*)` har
// sahifada butun jurnalni skanerlardi). Jadval `showTotal={false}` bilan
// chiziladi, "jami N" hech qayerda YOZILMAYDI.

import { z } from 'zod'
import { api } from '@/shared/api/client'
import type { PageResult } from '@/shared/ui/table/DataTable'
import { syntheticTotal } from '@/shared/api/pageResult'
import { instantSchema } from '@/shared/api/schemas'
import { currentIntlLocale } from '@/app/i18n'
import { shiftDay, startOfDayIso, todayInTenantZone } from '@/shared/lib/format'
import { formatBalanceBytes } from '@/shared/lib/format.units'
import type { TableState } from '@/shared/ui/table/tableState'
import {
  BYTE_UNIT,
  LEDGER_ACCOUNT_TYPES,
  LEDGER_OWNER_TYPES,
  hasNextPageSchema,
  ledgerIdSchema,
  looksLikeOwnerId,
} from './ledgerApi'
import { formatLedgerAmount } from './ledgerAmount'

/**
 * `ledger.journal.enums.TxnKind` NUSXASI — yopiq ro'yxat.
 *
 * ⚠️ Kontrollerda `@RequestParam TxnKind kind`, ya'ni ro'yxatdan tashqari
 * qiymat Spring darajasida 400 beradi va JADVAL BUTUNLAY xatoga tushadi.
 * Shuning uchun filtr qiymati serverga faqat shu ro'yxatdan chiqadi.
 */
export const JOURNAL_TXN_KINDS = [
  'OPENING_BALANCE',
  'TOPUP',
  'AGENT_DEPOSIT_FUNDING',
  'PACKAGE_SALE',
  'COMMISSION',
  'CASH_HANDOVER',
  'USAGE_CHARGE',
  'SUBSCRIPTION_CHARGE',
  'DEVICE_RENTAL',
  'REFUND',
  'ADJUSTMENT',
  'REVERSAL',
  'CASH_TRANSFER',
  'CASH_INCOME',
  'CASH_EXPENSE',
  'CASH_ADJUSTMENT',
  'DISCOUNT',
  'INVOICE_SALE',
] as const

/** `JournalSortField` — server saralay oladigan YAGONA ikki ustun */
const SORT_FIELDS: Readonly<Record<string, string>> = {
  createdAt: 'CREATED_AT',
  amount: 'AMOUNT',
}

/** Jadval ustuni kaliti serverning saralash ro'yxatida bormi */
export function journalSortable(columnKey: string): boolean {
  return columnKey in SORT_FIELDS
}

/**
 * Filtr kalitlari — `f.<kalit>` shaklida MANZILDA yashaydi.
 *
 * ⚠️ `q` bu yerda YO'Q: u `DataTable` ning prefikssiz qidiruv parametri
 * (`tableState.paramNames`) va allaqachon manzilda.
 */
export const JOURNAL_FILTER_KEYS = [
  'from',
  'to',
  'kind',
  'accountType',
  'ownerType',
  'ownerId',
  'unit',
  'scope',
] as const

/**
 * «Hammasi» qamrovi — `f.scope=all`.
 *
 * ⚠️ Sukut qiymat MANZILGA YOZILMAYDI: `/transactions` toza havolasi
 * har doim «faqat pul» degani bo'lsin, aks holda bir xil sahifaning ikki
 * xil manzili bo'lardi.
 */
export const JOURNAL_SCOPE_ALL = 'all'

const journalRowSchema = z.object({
  id: ledgerIdSchema,
  txnId: ledgerIdSchema,
  accountId: ledgerIdSchema,
  amount: z.number(),
  unit: z.string(),
  createdAt: instantSchema,
  /*
    ⚠️ `.nullish()`, `.nullable()` EMAS. Serverda
    `default-property-inclusion: non_null`, ya'ni bo'sh maydon JSON'da
    UMUMAN bo'lmaydi — `.nullable()` esa maydonning O'ZINI talab qiladi
    va zod butun sahifani rad etardi.
  */
  txnKind: z.string().nullish(),
  txnReference: z.string().nullish(),
  accountType: z.string().nullish(),
  ownerType: z.string().nullish(),
  ownerId: z.string().nullish(),
  txnNote: z.string().nullish(),
  counterAccountId: ledgerIdSchema.nullish(),
  counterAccountType: z.string().nullish(),
  counterOwnerType: z.string().nullish(),
  counterOwnerId: z.string().nullish(),
  counterCount: z.number().nullish(),
  money: z.boolean().nullish(),
})

export interface JournalRow {
  id: string
  txnId: string
  accountId: string
  /** Ishorali son, MINOR birlik: musbat = kredit, manfiy = debit */
  amount: number
  unit: string
  createdAt: string
  txnKind: string | null
  txnReference: string | null
  accountType: string | null
  ownerType: string | null
  ownerId: string | null
  txnNote: string | null
  counterAccountId: string | null
  counterAccountType: string | null
  counterOwnerType: string | null
  counterOwnerId: string | null
  /** Qarshi tomonlar SONI — `counter*` maydonlari faqat ENG KATTASINI beradi */
  counterCount: number
  /** `false` — summa PUL EMAS (trafik, `BYTE`) */
  money: boolean
}

/**
 * Yozuv PULMI.
 *
 * ⚠️ Serverning ta'rifi bilan AYNAN bir xil: `JournalRow.isMoney()` =
 * `!BYTE_UNIT.equals(unit)`. Maydon kelmasa (eski backend) shu ta'rif
 * qayta hisoblanadi — `true` deb taxmin qilish `BYTE` ni pul qilib
 * ko'rsatardi, ya'ni 1 GB ekranda "10 737 418,24 AFN" bo'lib chiqardi.
 */
export function isMoneyRow(raw: { unit: string; money?: boolean | null }): boolean {
  return raw.money ?? raw.unit.trim().toUpperCase() !== BYTE_UNIT
}

/**
 * Summani KO'RSATISH — `money` bayrog'i QAROR QILADI, `unit` emas.
 *
 * ⚠️ NEGA BU FUNKSIYA ALOHIDA VA NEGA QATORNI OLADI: chaqiruvchiga
 * `amount` va `unit` ni alohida berish `money` ni tushirib qoldirish
 * imkonini qoldirardi. Bir marta tushirib qoldirilsa trafik yozuvi pul
 * bo'lib ko'rinadi va farq MING BAROBAR bo'ladi — ekranda esa u
 * o'ziga o'xshab, ishonarli turadi.
 */
export function formatJournalAmount(row: {
  amount: number
  unit: string
  money?: boolean | null
}): string {
  if (isMoneyRow(row)) return formatLedgerAmount(row.amount, row.unit)
  if (row.unit.trim().toUpperCase() === BYTE_UNIT) return formatBalanceBytes(row.amount)
  // Pul ham, bayt ham emas — xom son + birlik. Taxmin qilinmaydi.
  return `${row.amount.toLocaleString(currentIntlLocale())} ${row.unit}`
}

/**
 * KO'RSATILMAYDIGAN qarshi tomonlar soni.
 *
 * ⚠️ `counterCount > 1` bo'lsa javobdagi `counter*` maydonlari qarshi
 * tomonning FAQAT eng kattasini beradi (`JournalRow` javadoc'i).
 * Qolganini jimgina yashirish pul jurnalida yolg'on bo'lardi — panel
 * "va yana N ta" deb aytadi.
 */
export function hiddenCounterparties(row: { counterCount: number }): number {
  return Math.max(0, row.counterCount - 1)
}

function toJournalRow(raw: z.infer<typeof journalRowSchema>): JournalRow {
  return {
    id: String(raw.id),
    txnId: String(raw.txnId),
    accountId: String(raw.accountId),
    amount: raw.amount,
    unit: raw.unit,
    createdAt: raw.createdAt,
    txnKind: raw.txnKind ?? null,
    txnReference: raw.txnReference ?? null,
    accountType: raw.accountType ?? null,
    ownerType: raw.ownerType ?? null,
    ownerId: raw.ownerId ?? null,
    txnNote: raw.txnNote ?? null,
    counterAccountId: raw.counterAccountId === null || raw.counterAccountId === undefined
      ? null
      : String(raw.counterAccountId),
    counterAccountType: raw.counterAccountType ?? null,
    counterOwnerType: raw.counterOwnerType ?? null,
    counterOwnerId: raw.counterOwnerId ?? null,
    // Maydon kelmasa: qarshi tomon bor bo'lsa kamida bitta, yo'q bo'lsa nol.
    // "1" deb qotirish qarshi tomonsiz yozuvda ham bitta bor deb aytardi.
    counterCount: raw.counterCount ?? (raw.counterAccountId === null || raw.counterAccountId === undefined ? 0 : 1),
    money: isMoneyRow(raw),
  }
}

const journalPageSchema = hasNextPageSchema(journalRowSchema)

function firstFilter(filters: TableState['filters'], key: string): string | null {
  const values = filters[key]
  return values && values.length > 0 ? (values[0] ?? null) : null
}

export interface JournalDayRange {
  /** `YYYY-MM-DD`, TENANT kalendari bo'yicha */
  from: string
  to: string
}

/**
 * Ko'rinadigan davr — SUKUT BO'YICHA BUGUNGI KUN.
 *
 * ⚠️ Server ham bo'sh davrni bugun bilan to'ldiradi (`TenantDayWindow`),
 * lekin panel buni O'ZI ham ko'rsatishi shart: aks holda tanlagich bo'sh
 * turar, ro'yxat esa qisqargan bo'lardi va operator "yozuvlar yo'qolibdi"
 * degan xulosaga kelardi.
 *
 * ⚠️ Kun TENANT zonasida (`todayInTenantZone`), brauzernikida EMAS —
 * `Asia/Kabul` UTC+04:30 va kun chegarasi kassirning kuni bilan mos
 * kelishi kerak (`dayRange.ts` dagi batafsil izoh).
 */
export function journalDayRange(filters: TableState['filters']): JournalDayRange {
  const today = todayInTenantZone()
  return {
    from: firstFilter(filters, 'from') ?? today,
    to: firstFilter(filters, 'to') ?? today,
  }
}

/** Faol (ro'yxatni TORAYTIRADIGAN) filtrlar soni — davr sukut bo'lsa sanalmaydi */
export function journalActiveFilterCount(filters: TableState['filters']): number {
  return (['kind', 'accountType', 'ownerType', 'ownerId', 'unit'] as const).filter(
    (key) => firstFilter(filters, key) !== null,
  ).length
}

export interface JournalScope {
  /** So'rovga ketadigan `moneyOnly` bayrog'i */
  moneyOnly: boolean
  /** Nega shunday — ekrandagi belgi AYNAN shu qiymatdan chiziladi */
  reason: 'default' | 'all' | 'unit'
  /** `reason === 'unit'` bo'lganda tanlangan birlik, aks holda `null` */
  unit: string | null
}

/**
 * Ro'yxat QAMROVI — sukut bo'yicha faqat pul (egasi talabi 2026-08-21).
 *
 * <h2>⚠️ ANIQ SO'RALGAN BIRLIK BAYROQDAN USTUN</h2>
 * `unit` tanlangan bo'lsa bayroq `false` ketadi. Serverning o'zi ham shu
 * qoidani qo'llaydi (`unit=BYTE&moneyOnly=true` → `BYTE` qatorlari), lekin
 * panel unga TAYANMAYDI: bayroq va birlik bir-biriga zid yuborilsa
 * «trafikni ko'rsat» so'rovining natijasi SERVERNING ichki qaroriga
 * bog'lanib qolardi va u o'zgargan kuni operator doim BO'SH ro'yxat
 * ko'rib, trafik yozuvlarini «yo'q» deb o'qirdi (ADR-033 §4).
 *
 * <h2>⚠️ SABAB QAYTARILADI, FAQAT BAYROQ EMAS</h2>
 * Ekrandagi belgi (`JournalScopeBanner`) shu `reason` dan chiziladi.
 * Ikkinchi joyda qayta hisoblansa belgi bilan so'rov birinchi
 * o'zgarishdayoq ajrashardi — ya'ni ekran «faqat pul» deb turib, ro'yxatda
 * trafik ko'rinardi.
 */
export function journalScope(filters: TableState['filters']): JournalScope {
  const unit = firstFilter(filters, 'unit')?.trim() ?? ''
  if (unit !== '') return { moneyOnly: false, reason: 'unit', unit: unit.toUpperCase() }
  if (firstFilter(filters, 'scope') === JOURNAL_SCOPE_ALL) {
    return { moneyOnly: false, reason: 'all', unit: null }
  }
  return { moneyOnly: true, reason: 'default', unit: null }
}

/**
 * Jadval holatidan SERVER parametrlarini yasaydi.
 *
 * ⚠️ FUNKSIYA ATAYLAB `fetchJournalRows` DAN AJRATILGAN: qorovul
 * naqsh qidirmasdan uni CHAQIRIB tekshiradi (`tests/closed-filter.mjs`
 * dagi bir xil saboq — naqsh o'tadigan, xulqi noto'g'ri kod yozish oson).
 *
 * ⚠️ Har enum qiymat YOPIQ RO'YXAT bo'yicha tekshiriladi. Manzilga qo'lda
 * yozilgan `f.kind=SALOM` serverda 400 berardi, ya'ni bitta xato harf
 * butun jadvalni o'chirardi.
 */
export function journalListParams(state: TableState): Record<string, unknown> {
  const range = journalDayRange(state.filters)
  const kind = firstFilter(state.filters, 'kind')
  const accountType = firstFilter(state.filters, 'accountType')
  const ownerType = firstFilter(state.filters, 'ownerType')
  const ownerId = firstFilter(state.filters, 'ownerId')
  /*
    ⚠️ Birlik ham, bayroq ham BIR MANBADAN. Ilgari birlik shu yerda
    alohida o'qilar edi va bo'sh joydan iborat qiymat serverga `unit=`
    bo'lib ketardi; qamrov esa uni «tanlanmagan» deb bilardi — bitta
    holatning ikki xil ta'rifi.
  */
  const scope = journalScope(state.filters)
  const search = state.search.trim()
  const sortField = state.sortField === null ? null : (SORT_FIELDS[state.sortField] ?? null)

  return {
    from: startOfDayIso(range.from),
    /*
      ⚠️ Yuqori chegara KIRITILMAYDI (`JournalQuery`: "to — oraliq oxiri,
      KIRITILMAYDI"). Shuning uchun KEYINGI kun boshi yuboriladi: kun
      oxirini (`23:59:59.999`) berish oxirgi millisekunddagi yozuvni
      tushirib qoldirardi va bu farq faqat sanoq mos kelmaganda
      ko'rinardi.
    */
    to: startOfDayIso(shiftDay(range.to, 1)),
    ...(kind && JOURNAL_TXN_KINDS.includes(kind as (typeof JOURNAL_TXN_KINDS)[number])
      ? { kind }
      : {}),
    ...(accountType &&
    LEDGER_ACCOUNT_TYPES.includes(accountType as (typeof LEDGER_ACCOUNT_TYPES)[number])
      ? { accountType }
      : {}),
    ...(ownerType && LEDGER_OWNER_TYPES.includes(ownerType as (typeof LEDGER_OWNER_TYPES)[number])
      ? { ownerType }
      : {}),
    /*
      ⚠️ Kontrollerda `@RequestParam UUID ownerId` — Spring yaroqsiz
      satrni O'ZI rad etadi (400). Shakli to'g'ri kelmagan qiymat
      SERVERGA UMUMAN YUBORILMAYDI, sahifa esa buning sababini yozadi.
    */
    ...(ownerId && looksLikeOwnerId(ownerId) ? { ownerId } : {}),
    ...(scope.unit === null ? {} : { unit: scope.unit }),
    ...(search ? { q: search } : {}),
    /*
      ⚠️ BAYROQ HAR DOIM ANIQ YUBORILADI — `sortField` bilan bir xil sabab.
      Serverning sukuti `false`, lekin uni AYTMASLIK «faqat pul» qarorini
      serverning ichki qaroriga bog'lardi. Mijozda kesish TAQIQ (ro'yxat
      serverda sahifalangan), ya'ni bu bitta parametr — yagona vosita.
    */
    moneyOnly: scope.moneyOnly,
    /*
      ⚠️ TARTIB HAR DOIM ANIQ YUBORILADI. Serverning o'z sukuti ham
      `CREATED_AT DESC`, lekin uni AYTMASLIK "oxirgi qo'shilgan birinchi"
      qoidasini serverning ichki qaroriga bog'lab qo'yardi — u
      o'zgargan kuni panel jimgina boshqa tartibda chizilardi.
    */
    sortField: sortField ?? 'CREATED_AT',
    sortOrder: sortField !== null && state.sortOrder === 'ascend' ? 'ASC' : 'DESC',
    page: state.page - 1,
    size: state.pageSize,
  }
}

/**
 * Manzildagi ODDIY parametrlarni jadval holatiga ko'chiradi.
 *
 * ⚠️ NEGA KERAK: abonent kartasidagi «To'lovlari» havolasi sahifaga
 * `?ownerType=SUBSCRIBER&ownerId=<uuid>` bilan keladi, jadval holati esa
 * `f.` prefiksi bilan yashaydi (`tableState.paramNames`). Ikkalasini
 * yonma-yon qoldirish eng yomon holat bo'lardi: «Tozalash» tugmasi
 * `f.` larni o'chirar, prefikssizlari esa MANZILDA QOLIB, ro'yxatni
 * torayrirgan holda ushlab turardi — operator uchun bu "tozaladim,
 * lekin ro'yxat baribir qisqa" degan javobsiz savol.
 *
 * `null` — ko'chiriladigan narsa yo'q (odatdagi holat).
 */
export function normalizeJournalParams(params: URLSearchParams): URLSearchParams | null {
  const next = new URLSearchParams(params)
  let changed = false
  for (const key of JOURNAL_FILTER_KEYS) {
    const plain = next.get(key)
    if (plain === null) continue
    next.delete(key)
    changed = true
    // `f.` allaqachon bo'lsa u USTUN: u foydalanuvchining oxirgi tanlovi
    if (plain !== '' && next.get(`f.${key}`) === null) next.set(`f.${key}`, plain)
  }
  return changed ? next : null
}

export async function fetchJournalRows(
  state: TableState,
  signal: AbortSignal,
): Promise<PageResult<JournalRow>> {
  const response = await api.get<unknown>('/api/ledger/entries', {
    signal,
    params: journalListParams(state),
  })
  const parsed = journalPageSchema.parse(response.data)
  return {
    items: parsed.rows.map(toJournalRow),
    // ⚠️ SUN'IY son — faqat sahifa tugmalari uchun. `showTotal={false}`
    // bo'lgani uchun u ekranda "jami" bo'lib CHIZILMAYDI (`pageResult.ts`).
    total: syntheticTotal(state.page, state.pageSize, parsed.rows.length, parsed.hasNext),
  }
}
